// What can be done to an open order from more than one screen (the order's own
// screen and the Orders tab's list): print the guest check, send to the kitchen.
import { kotLines, orderLines, sendToKitchen, setOrderBilled, type SyncOp } from '@ims/core'
import type {
  InvoiceDetail,
  InvoiceRow,
  OrderDetail,
  Repository,
  TenantSettingsRow,
} from '@ims/data'
import type { useToast } from '@ims/ui'
import type { ReactNode } from 'react'
import { displayName } from '../lib/account.ts'
import { billAdjustments, type BillAdjustments } from '../lib/bill-adjustments.ts'
import type { PrintedBy } from '../lib/doc-view.ts'
import { useSession } from '../session.tsx'
import { nextPrint } from '../lib/print-count.ts'
import { invoicePrintOp } from '../lib/rows.ts'
import { todayIso } from '../lib/format.ts'
import {
  STATION_LABEL,
  clock,
  orderItemRows,
  orderRow,
  orderServiceFor,
  startOfToday,
  type BillService,
} from '../lib/orders.ts'
import {
  GuestCheck,
  InvoiceA4,
  KotSlips,
  Receipt,
  type BatchLabels,
  type BillPrintMeta,
  printDocument,
  printName,
  type KotSlipData,
  type PrintFormat,
} from '../print.tsx'

export interface OrderActionDeps {
  repo: Repository
  settings: TenantSettingsRow | null | undefined
  toast: ReturnType<typeof useToast>
  context: () => { tenantId: string; now: string }
  write: (ops: () => SyncOp[]) => Promise<boolean>
}

/** Sends what is new to the kitchen: a ticket per station, a toast, and the slips if set. */
export async function sendOrderToKitchen(
  { repo, settings, toast, context, write }: OrderActionDeps,
  detail: OrderDetail,
  where: string | null,
): Promise<void> {
  const last = await repo.orders.lastKotNo(startOfToday())
  const built = sendToKitchen(
    context(),
    orderRow(detail.order),
    orderItemRows(detail.items),
    detail.sent,
    last + 1,
  )
  if (built.tickets.length === 0) return
  if (!(await write(() => built.ops))) return
  toast.success(
    `Sent to ${built.tickets.map((t) => `${STATION_LABEL[t.ticket.station].toLowerCase()} (#${t.ticket.kot_no})`).join(' and ')}`,
  )
  if (settings?.kot_print) {
    printDocument(
      <KotSlips
        slips={built.tickets.map((t): KotSlipData => ({
          kotNo: t.ticket.kot_no,
          station: STATION_LABEL[t.ticket.station],
          tableName: where,
          time: clock(t.ticket.sent_at),
          lines: kotLines(t.rows),
        }))}
      />,
      'receipt',
    )
  }
}

/** Prints the guest check; the order then waits to be paid ("bill printed"). */
export async function printGuestCheck(
  { settings, context, write }: OrderActionDeps,
  detail: OrderDetail,
  where: string | null,
): Promise<void> {
  const order = orderRow(detail.order)
  if (order.status !== 'billed' && !(await write(() => setOrderBilled(context(), order, true).ops)))
    return
  printDocument(
    <GuestCheck
      lines={orderLines(orderItemRows(detail.items))}
      tableName={where}
      covers={order.covers}
      date={todayIso()}
      time={clock(new Date().toISOString())}
      settings={settings}
    />,
    'receipt',
  )
}

/**
 * Every print of a bill goes through here (docs/plans/print-copies.md): the first print of
 * an issued bill is the original, each later one, in either format and from any device, is
 * "COPY OF ORIGINAL - N". The paper comes first; recording the print is its own tx after,
 * and a failure there goes to `onRecordError`, never stopping the printer. Drafts are
 * neither counted nor marked.
 */
export async function printBill(
  repo: Repository,
  tenantId: string,
  invoice: InvoiceRow,
  format: PrintFormat,
  render: (copyLabel: string | null) => ReactNode,
  onRecordError?: (error: unknown) => void,
): Promise<void> {
  const issued = invoice.status !== 'draft'
  const prints = issued
    ? (await repo.list('invoice_prints', { where: { invoice_id: invoice.id }, limit: 1000 })).rows
    : []
  const next = issued ? nextPrint(invoice, prints) : { copyNo: 0, label: null }
  printDocument(
    render(next.label),
    format,
    printName(format === 'a4' ? 'Invoice' : 'Receipt', invoice),
  )
  if (!issued) return
  try {
    await repo.commit([
      invoicePrintOp({ tenantId }, { invoice_id: invoice.id, format, copy_no: next.copyNo }),
    ])
  } catch (error) {
    onRecordError?.(error)
  }
}

/** Everything a bill's print or PDF needs, read now (the invoice screen keeps its own copy). */
export interface BillPrintData {
  detail: InvoiceDetail
  service: BillService | null
  adjustments: BillAdjustments | null
  batches: BatchLabels
}

export async function loadBillPrint(
  repo: Repository,
  invoiceId: string,
  serverName: (id: string | null) => string | null,
): Promise<BillPrintData | null> {
  const detail = await repo.invoices.detail(invoiceId)
  if (!detail) return null
  const service = await orderServiceFor(repo, detail.invoice, serverName)
  const credits = (await repo.list('credit_notes', { where: { invoice_id: invoiceId }, limit: 50 }))
    .rows
  const batches: BatchLabels = {}
  for (const id of new Set(detail.lines.map((l) => l.batch_id))) {
    const batch = id ? await repo.get('batches', id) : null
    if (batch) batches[batch.id] = { batch_no: batch.batch_no, expiry_date: batch.expiry_date }
  }
  return {
    detail,
    service,
    adjustments: billAdjustments(detail.invoice, detail.balancePaisa, credits),
    batches,
  }
}

/** The bill as printed on `format`: the A4 bill or the 80 mm receipt. */
export function billDocument(
  format: PrintFormat,
  data: BillPrintData,
  settings: TenantSettingsRow | null | undefined,
  copyLabel: string | null,
  meta: BillPrintMeta,
): ReactNode {
  const props = {
    invoice: data.detail.invoice,
    lines: data.detail.lines,
    settings,
    balancePaisa: data.detail.balancePaisa,
    batches: data.batches,
    adjustments: data.adjustments,
    copyLabel,
    service: data.service,
    meta,
  }
  return format === 'a4' ? <InvoiceA4 {...props} /> : <Receipt {...props} />
}

/** Who prints a bill and when, the order it came from, and the QR's link. */
export function useBillMeta(): (repo: Repository, invoice: InvoiceRow) => Promise<BillPrintMeta> {
  const { platform } = useSession()
  const printedBy = usePrintedBy()
  return async (repo, invoice) => ({
    ...printedBy(),
    apiUrl: platform.apiUrl,
    reference: invoice.sales_order_id
      ? ((await repo.get('sales_orders', invoice.sales_order_id))?.number ?? null)
      : null,
  })
}

/** Who is printing, and now: for the "Printed by" line of any document. */
export function usePrintedBy(): () => PrintedBy {
  const { state } = useSession()
  const who = state.status === 'signedIn' ? displayName(state.user) : null
  return () => ({ printedBy: who, printedAt: new Date().toISOString() })
}

/** The copy a print or PDF of `invoice` made now will be (`nextPrint`). */
export async function nextCopy(
  repo: Repository,
  invoice: InvoiceRow,
): Promise<{ copyNo: number; label: string | null }> {
  if (invoice.status === 'draft') return { copyNo: 0, label: null }
  const prints = (
    await repo.list('invoice_prints', { where: { invoice_id: invoice.id }, limit: 1000 })
  ).rows
  return nextPrint(invoice, prints)
}

/** Records a print (or a PDF, which is a copy too) of an issued bill as its own tx. */
export async function recordPrint(
  repo: Repository,
  invoice: InvoiceRow,
  format: PrintFormat,
  copyNo: number,
): Promise<void> {
  if (invoice.status === 'draft') return
  await repo.commit([
    invoicePrintOp(
      { tenantId: invoice.tenant_id },
      { invoice_id: invoice.id, format, copy_no: copyNo },
    ),
  ])
}
